import React, { useState } from "react";
import { X } from "lucide-react";
import { INGREDIENTS } from "@/data/ingredients";
import { LeafGlyph } from "@/components/BotanicalBackdrop";
import BotanicalIcon from "@/components/BotanicalIcon";

// A luxury apothecary-catalogue composition — the whole card top area is a
// premium sepia gradient with a large stylised botanical illustration.
// Feels like a museum display card, never breaks, always on-brand.
const IngredientVisual = ({ ing, size = "large" }) => (
  <div
    className="relative w-full h-full overflow-hidden"
    style={{
      background:
        `radial-gradient(circle at 30% 20%, ${ing.accent}22, transparent 65%),` +
        `radial-gradient(circle at 70% 80%, ${ing.accent}18, transparent 60%),` +
        `linear-gradient(160deg, #F5EFE4 0%, #EFE6D4 60%, ${ing.accent}22 100%)`,
    }}
    aria-hidden="true"
  >
    {/* Faint concentric rings */}
    <div className="absolute inset-6 rounded-full border" style={{ borderColor: `${ing.accent}25` }} />
    <div className="absolute inset-12 rounded-full border" style={{ borderColor: `${ing.accent}18` }} />
    {/* Botanical illustration */}
    <div
      className={
        "absolute inset-0 flex items-center justify-center " +
        (size === "large" ? "p-6 md:p-8" : "p-4")
      }
      style={{ color: ing.accent }}
    >
      <div className={size === "large" ? "w-32 h-32 md:w-40 md:h-40" : "w-20 h-20"}>
        <BotanicalIcon variant={ing.iconVariant} />
      </div>
    </div>
    {/* Corner glyph */}
    <div className="absolute top-3 right-3 opacity-60" style={{ color: ing.accent }}>
      <LeafGlyph className="w-4 h-4" />
    </div>
  </div>
);

const IngredientCard = ({ ing, onClick, delayMs }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`ingredient-card-${ing.id}`}
      className="hk-card overflow-hidden text-left flex flex-col group hk-fade-up"
      style={{ animationDelay: `${delayMs}ms` }}
    >
      <div className="relative aspect-square">
        <IngredientVisual ing={ing} size="small" />
        <div className="absolute inset-0 bg-gradient-to-t from-[rgba(30,35,31,0.35)] via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        <div className="absolute top-3 left-3">
          <span className="px-2.5 py-1 rounded-full bg-white/95 backdrop-blur text-[10px] tracking-[0.2em] uppercase text-[var(--hk-ink)] border border-[var(--hk-border)]">
            {ing.category}
          </span>
        </div>
      </div>
      <div className="p-5">
        <div className="label-eyebrow">{ing.latin}</div>
        <h3 className="serif mt-1.5 text-[20px] leading-tight text-[var(--hk-ink)]">{ing.name}</h3>
        <p className="mt-2 text-[13px] leading-[1.65] text-[var(--hk-ink-2)] line-clamp-2">{ing.short}</p>
      </div>
    </button>
  );
};

const IngredientModal = ({ ing, onClose }) => {
  if (!ing) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm hk-fade-up"
      onClick={onClose}
      data-testid="ingredient-modal"
    >
      <div
        className="relative bg-[var(--hk-ivory)] rounded-[16px] max-w-2xl w-full overflow-hidden shadow-[0_30px_60px_-20px_rgba(0,0,0,0.4)]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-white/95 backdrop-blur border border-[var(--hk-border)] flex items-center justify-center text-[var(--hk-ink)] hover:bg-white transition-colors"
          data-testid="ingredient-modal-close"
          aria-label="Close"
        >
          <X size={16} />
        </button>
        <div className="grid md:grid-cols-2">
          <div className="aspect-square md:aspect-auto min-h-[320px] relative">
            <IngredientVisual ing={ing} size="large" />
          </div>
          <div className="p-8 md:p-10">
            <div className="label-eyebrow">{ing.category}</div>
            <h2 className="serif mt-2 text-[28px] leading-tight text-[var(--hk-ink)]">{ing.name}</h2>
            <p className="italic-accent text-[var(--hk-orange-deep)] text-[15px] mt-1">{ing.latin}</p>
            <p className="mt-5 text-[14px] leading-[1.85] text-[var(--hk-ink-2)]">{ing.note}</p>
            <div className="mt-6 pt-6 border-t border-[var(--hk-border)]">
              <div className="label-eyebrow flex items-center gap-2">
                <LeafGlyph className="w-3 h-3 text-[var(--hk-gold-deep)]" />
                Classical Reference
              </div>
              <p className="mt-2 text-[13px] leading-[1.75] text-[var(--hk-ink-2)]">{ing.tradition}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const IngredientLibrary = () => {
  const [active, setActive] = useState(null);
  return (
    <div className="relative">
      <div className="max-w-3xl mb-10">
        <div className="label-eyebrow">The Botanical Library</div>
        <h2 className="serif mt-3 text-[32px] sm:text-[40px] lg:text-[46px] leading-[1.05] text-[var(--hk-ink)]">
          Thirteen botanicals,
          <br />
          <span className="italic-accent text-[var(--hk-orange-deep)]">one considered</span> composition.
        </h2>
        <p className="mt-5 text-[15px] leading-[1.85] text-[var(--hk-ink-2)] max-w-xl">
          Explore each botanical guiding the MAN 360 formulation. Click any card for a closer look at the ingredient and its place in classical Ayurveda.
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 lg:gap-6" data-testid="ingredient-library-grid">
        {INGREDIENTS.map((ing, idx) => (
          <IngredientCard key={ing.id} ing={ing} delayMs={idx * 40} onClick={() => setActive(ing)} />
        ))}
      </div>

      <IngredientModal ing={active} onClose={() => setActive(null)} />
    </div>
  );
};

export default IngredientLibrary;
